// Kapitánský plánovač — matice kapitánů GW × hráč dle Prediction Modelu v2.
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Crown, MagnifyingGlass, ArrowsClockwise, Info } from "@phosphor-icons/react";
import AppShell from "../components/AppShell";
import { getCaptainPlan } from "../lib/api";
import { track } from "../lib/analytics";

const STORAGE_KEY = "fa.planner.last_manager_id";
const RANGES = [
    { value: 6, label: "6 GW" },
    { value: 12, label: "12 GW" },
    { value: 38, label: "Do GW38" },
];
const CONF = { HIGH: "text-fa-mint", MEDIUM: "text-fa-yellow", LOW: "text-white/40" };

function heat(exp, max) {
    if (exp == null || max <= 0) return "transparent";
    const t = Math.min(exp / max, 1);
    return `rgba(158,232,180,${(0.04 + t * 0.32).toFixed(3)})`;
}

export default function Captain() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [range, setRange] = useState(12);
    const [count, setCount] = useState(12);
    const [managerInput, setManagerInput] = useState(() => localStorage.getItem(STORAGE_KEY) || "");
    const [managerId, setManagerId] = useState(null);

    useEffect(() => { track("tool_open", { tool: "captain_planner" }); }, []);

    useEffect(() => {
        setLoading(true);
        const gwTo = range === 38 ? 38 : undefined;
        getCaptainPlan({
            gw_to: gwTo ?? Math.min((data?.gw_from || 1) + range - 1, 38),
            candidates: count,
            manager_id: managerId || undefined,
        })
            .then(setData)
            .catch((e) => {
                toast.error(e.response?.data?.detail || "Načtení plánovače selhalo");
                if (managerId) setManagerId(null);
            })
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [range, count, managerId]);

    const maxExp = useMemo(() => {
        let m = 0;
        (data?.gws || []).forEach((g) => g.cells.forEach((c) => { if (c && c.exp > m) m = c.exp; }));
        return m;
    }, [data]);

    const loadSquad = () => {
        const id = parseInt(managerInput, 10);
        if (!id) { toast.error("Zadej platné FPL Manager ID"); return; }
        localStorage.setItem(STORAGE_KEY, String(id));
        setManagerId(id);
    };

    return (
        <AppShell>
            <div className="space-y-5" data-testid="captain-page">
                <header className="border-2 border-fa-mint/40 bg-gradient-to-br from-fa-mint/10 via-transparent to-transparent p-6">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-fa-mint flex items-center gap-2">
                        <Crown size={11} weight="fill" /> Manager Lab · Play
                    </div>
                    <h1 className="font-display font-black uppercase tracking-tight text-3xl md:text-4xl mt-1">Kapitánský plánovač</h1>
                    <p className="font-mono text-[11px] text-white/65 mt-2 max-w-3xl">
                        Matice kapitánů dle Prediction Modelu v2 — pro každé kolo vidíš očekávané body kandidátů a nejlepší volbu (C = 2× body).
                        Zadej FPL Manager ID a plánuj kapitány jen ze svého týmu.
                    </p>
                </header>

                <div className="flex flex-wrap items-center gap-2" data-testid="captain-controls">
                    {RANGES.map((r) => (
                        <button key={r.value} type="button" onClick={() => setRange(r.value)} data-testid={`captain-range-${r.value}`}
                                className={`font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 border transition-colors ${range === r.value ? "border-fa-mint bg-fa-mint/15 text-fa-mint" : "border-white/15 text-white/60 hover:border-white/40"}`}>
                            {r.label}
                        </button>
                    ))}
                    <select value={count} onChange={(e) => setCount(Number(e.target.value))} data-testid="captain-count-select"
                            className="bg-black/40 border border-white/15 px-2 py-1.5 font-mono text-[10px] focus:outline-none focus:border-fa-mint">
                        {[8, 12, 16, 20].map((n) => <option key={n} value={n}>{n} kandidátů</option>)}
                    </select>
                    <div className="flex items-center gap-1.5 ml-auto">
                        <input value={managerInput} onChange={(e) => setManagerInput(e.target.value)} placeholder="FPL Manager ID…"
                               onKeyDown={(e) => e.key === "Enter" && loadSquad()}
                               className="bg-black/40 border border-white/15 px-3 py-1.5 font-mono text-[11px] w-40 focus:outline-none focus:border-fa-mint placeholder:text-white/25"
                               data-testid="captain-manager-input" />
                        <button type="button" onClick={loadSquad} data-testid="captain-manager-load"
                                className="font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 border border-fa-mint/60 text-fa-mint hover:bg-fa-mint hover:text-black flex items-center gap-1">
                            <MagnifyingGlass size={11} /> Můj tým
                        </button>
                        {managerId && (
                            <button type="button" onClick={() => setManagerId(null)} data-testid="captain-manager-clear"
                                    className="font-mono text-[10px] uppercase tracking-widest px-2.5 py-1.5 border border-white/15 text-white/50 hover:text-white">
                                × Celá liga
                            </button>
                        )}
                    </div>
                </div>

                {data?.squad_only && (
                    <div className="font-mono text-[10px] text-fa-mint border border-fa-mint/30 bg-fa-mint/5 px-3 py-2 flex items-center gap-2" data-testid="captain-squad-badge">
                        <Info size={12} /> Zobrazeni pouze hráči z týmu manažera {managerId}
                    </div>
                )}

                {loading && (
                    <div className="border border-white/10 bg-[#0d0d0d] p-8 font-mono text-[11px] text-white/50 flex items-center gap-2" data-testid="captain-loading">
                        <ArrowsClockwise size={13} className="animate-spin" /> Počítám predikce pro celou sezónu… (první načtení ~20 s)
                    </div>
                )}

                {!loading && data && (
                    <div className="border border-white/10 bg-[#0d0d0d] overflow-x-auto" data-testid="captain-matrix">
                        <table className="text-left border-collapse">
                            <thead>
                                <tr className="border-b border-white/15">
                                    <th className="px-3 py-2 font-mono text-[9px] uppercase tracking-widest text-white/40 sticky left-0 bg-[#0d0d0d] z-10">GW</th>
                                    {data.candidates.map((c) => (
                                        <th key={c.fpl_id} className="px-2 py-2 min-w-[86px]" data-testid={`captain-col-${c.fpl_id}`}>
                                            <Link to={`/players/${c.fpl_id}`} className="font-display font-bold text-[11px] leading-tight hover:text-fa-mint block">{c.name}</Link>
                                            <div className="font-mono text-[8px] text-white/40">{c.team} · {c.position} · £{c.price}</div>
                                            <div className={`font-mono text-[8px] ${CONF[c.confidence] || "text-white/40"}`}>{c.confidence} · vl. {c.ownership_pct}%</div>
                                        </th>
                                    ))}
                                    <th className="px-3 py-2 font-mono text-[9px] uppercase tracking-widest text-fa-mint min-w-[130px]">Kapitán kola</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.gws.map((g) => (
                                    <tr key={g.gw} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`captain-row-${g.gw}`}>
                                        <td className="px-3 py-1.5 font-mono text-[10px] text-white/60 tabular-nums sticky left-0 bg-[#0d0d0d] z-10">GW{g.gw}</td>
                                        {g.cells.map((cell, i) => {
                                            const cid = data.candidates[i].fpl_id;
                                            const isBest = cell && g.best?.fpl_id === cid;
                                            return (
                                                <td key={cid} className={`px-2 py-1.5 tabular-nums ${isBest ? "outline outline-1 outline-fa-mint/70" : ""}`}
                                                    style={{ background: heat(cell?.exp, maxExp) }}
                                                    title={cell ? `${cell.opp} ${cell.home ? "(doma)" : "(venku)"} · FDR ${cell.fdr}` : "bez zápasu"}>
                                                    {cell ? (
                                                        <>
                                                            <div className={`font-mono text-[11px] font-bold ${isBest ? "text-fa-mint" : "text-white/80"}`}>
                                                                {cell.exp}{isBest && <span className="ml-1 text-[8px] border border-fa-mint/70 px-0.5">C</span>}
                                                            </div>
                                                            <div className="font-mono text-[8px] text-white/35 uppercase">
                                                                {String(cell.opp).slice(0, 3)}{cell.home ? " (D)" : " (V)"}
                                                            </div>
                                                        </>
                                                    ) : <span className="font-mono text-[10px] text-white/20">—</span>}
                                                </td>
                                            );
                                        })}
                                        <td className="px-3 py-1.5">
                                            {g.best && (
                                                <div className="font-mono text-[10px]">
                                                    <span className="text-fa-mint font-bold">{g.best.name}</span>
                                                    <span className="text-white/45 ml-1.5 tabular-nums">2× = {(g.best.exp * 2).toFixed(1)}</span>
                                                    {g.differential && g.differential.fpl_id !== g.best.fpl_id && (
                                                        <div className="text-fa-sky text-[8px] mt-0.5">DIF: {g.differential.name} ({g.differential.exp})</div>
                                                    )}
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr className="border-t border-white/15">
                                    <td className="px-3 py-2 font-mono text-[8px] uppercase tracking-widest text-white/40 sticky left-0 bg-[#0d0d0d]">Σ / #C</td>
                                    {data.candidates.map((c) => (
                                        <td key={c.fpl_id} className="px-2 py-2 font-mono text-[10px] tabular-nums">
                                            <span className="text-white/70">{c.total_exp}</span>
                                            {c.best_count > 0 && <span className="text-fa-mint ml-1.5">{c.best_count}×C</span>}
                                        </td>
                                    ))}
                                    <td />
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                )}

                <p className="font-mono text-[9px] text-white/35 max-w-3xl">
                    Očekávané body = Prediction Model v2 (FDR profily, konzistence, výbušnost, haul rate, fáze sezóny, minuty, příprava).
                    Zvýrazněná buňka = nejlepší kapitán kola. DIF = nejlepší diferenciál s vlastnictvím pod 10 %.
                </p>
            </div>
        </AppShell>
    );
}
